import * as recall from '@otter-ai/recall-client';
import { MINUTE_MS } from '@/constants/time';

export type GalleryTemplateQuery = {
  readonly queryKey: ['template-details', string | undefined];
  readonly queryFn: () => Promise<recall.GalleryTemplate | null>;
  readonly staleTime: number;
  readonly enabled: boolean;
};

/**
 * Query creator for fetching a gallery template by base template type
 *
 * @example
 * ```tsx
 * const { data: galleryTemplate } = useQuery(galleryTemplateQueryCreator('meeting-notes'));
 *
 * // with queryClient
 * const galleryTemplate = await queryClient.fetchQuery(galleryTemplateQueryCreator('meeting-notes'));
 * queryClient.invalidateQueries(galleryTemplateQueryCreator('meeting-notes'));
 * queryClient.setQueryData(galleryTemplateQueryCreator('meeting-notes').queryKey, newGalleryTemplateData);
 * ```
 *
 * @param baseTemplateType The base template type
 * @param additionalOptions Additional options that can be passed to useQuery
 */
export function galleryTemplateQueryCreator(
  baseTemplateType: string | undefined,
): GalleryTemplateQuery {
  return {
    queryKey: ['template-details', baseTemplateType],
    queryFn: async () => {
      if (!baseTemplateType) return null;
      const response = await recall.getGalleryTemplate({
        query: { base_template_type: baseTemplateType },
      });
      if (response.error) {
        throw response.error;
      }
      return response.data?.gallery_template ?? null;
    },
    enabled: !!baseTemplateType,
    staleTime: 10 * MINUTE_MS,
  };
}
